import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AssignmentKind, AssignmentPreview } from '@testflow/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { type AssignmentMoveBody, assignmentApi } from '../../lib/assignment-api.js';
import { type TestCaseListItem, catalogApi } from '../../lib/catalog-api.js';
import { mapApiError } from '../../lib/errors.js';
import { pickLang } from '../../lib/pick-lang.js';
import { matchesQuery } from '../../lib/row-filter.js';
import { sourceSearchTerms } from '../../lib/source-label.js';
import { type StructureNode, structureApi } from '../../lib/structure-api.js';
import { useCurrentProject } from '../../lib/use-project.js';
import { useRowSelection } from '../../lib/use-row-selection.js';
import { useShowSourceRef } from '../../lib/use-source-ref.js';
import { useUrlState } from '../../lib/use-url-state.js';
import {
  type RightRow,
  TestCaseCells,
  TestCaseHeadCells,
  TestCaseSidePanel,
} from './testcase-rows.js';

// TF-006 (#112, A7) — Seite „Zuordnung".
//
// Sie heißt bewusst NICHT „Struktur": das Admin-Panel heißt bereits „Projektstruktur", und zwei
// Dinge unter einem Namen sind genau der Fehler, den dieses Feature auflöst. Arbeitsteilung:
//   Admin-Panel „Projektstruktur" → Knoten ANLEGEN, umbenennen, löschen (project.configure)
//   diese Seite                   → Inhalte UMHÄNGEN (catalog.edit)
//
// EIN Bedienmuster für alle drei Kanten (Architektur-Leitentscheidung 3):
//   auswählen → Ziel wählen → Vorschau mit Folgen → ausführen → Ergebnismeldung mit Teilerfolg.
// Kein Dialog weicht davon ab; deshalb rendert `MoveBar` + `PreviewDialog` alle drei Fälle und
// nicht drei fast gleiche Varianten.
//
// „Stückzahlen statt Inhalten" (A7): Prozess- und Kettenzeilen zeigen je Zeile eine Zahl, keine
// Details — man muss nichts aufklappen, um umzuhängen. NUR die Testfall-Liste ist davon
// ausgenommen (#153): 117 Testfälle ohne Prozess lassen sich allein an Titel + ID nicht zuordnen.
// Dort stehen Schritte, Bereitschaft, Ketten und Herkunft, dazu Suche und eine Seitenleiste mit
// der bearbeitbaren Katalog-Detailansicht (`TestCaseDetail`, kein Nachbau).
//
// Auswahl nur über Sichtbares (#91/#153): die Suche blendet Zeilen aus, wählt sie aber nicht ab.
// Zählung, Vorschau und Ausführen arbeiten deshalb immer auf „ausgewählt UND sichtbar" —
// sonst träfe eine Sammelaktion Zeilen, die der Nutzer gerade nicht sieht.
//
// KEIN Undo (bewusste Entscheidung, A7): Zurückhängen geht mit demselben Werkzeug. Ein echtes
// Rückgängig müsste widerrufene Abnahmen wiederbeleben und dabei entscheiden, welche Unterschrift
// nach einem Hin-und-Zurück wieder gilt — diese Frage hat keine ehrliche Antwort.

type View = 'teilprojekt' | 'stream';

// Zielwert der Option „Neuer Prozess …" in der Zielauswahl. Darf mit keiner Prozess-/Knoten-Id
// kollidieren (alles uuids) und nicht '' sein: der leere Wert heißt „Zuordnung lösen".
const NEW_PROCESS_TARGET = '__new__';

/**
 * Vorbelegung des Teilprojekts für einen neuen Prozess (#153): haben alle gewählten Testfälle
 * dasselbe Import-Teilprojekt (`source_teilprojekt`) UND gibt es einen Teilprojekt-Knoten dieses
 * Namens, ist das fast sicher das gewollte Ziel — der Normalfall sind 117 Testfälle aus einem Excel.
 * Gemischte oder unbekannte Herkunft bleibt leer: dann rät die Seite nicht. Die Schreibweise ist
 * egal (trim, Groß/Klein), weil der Name aus einer Excel-Zelle stammt und der Knoten von Hand
 * angelegt wurde.
 */
function prefillTeilprojekt(tcs: TestCaseListItem[], teilprojekte: StructureNode[]): string | null {
  const names = new Set(tcs.map((tc) => tc.source_teilprojekt?.trim().toLowerCase() ?? ''));
  if (names.size !== 1) return null;
  const [name] = names;
  if (!name) return null;
  return teilprojekte.find((n) => n.name.trim().toLowerCase() === name)?.node_id ?? null;
}

/** Was gerade links ausgewählt ist — daraus folgt, WAS rechts steht und WELCHE Kante gilt. */
type LeftSelection =
  | { kind: 'node'; nodeId: string | null } // Teilprojekt/Stream (null = Restmenge)
  | { kind: 'process'; processId: string | null }; // Prozess (null = „Nicht zugeordnet")

export function AssignmentPage() {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage ?? 'de';
  const { project, isLoading: projectLoading } = useCurrentProject();
  const projectId = project?.project_id ?? '';
  const queryClient = useQueryClient();

  // Sicht in der URL — deep-linkbar und reload-fest, wie im Katalog (#36/A3).
  const [viewParam, setViewParam] = useUrlState('view', 'teilprojekt');
  const view: View = viewParam === 'stream' ? 'stream' : 'teilprojekt';
  const [left, setLeft] = useState<LeftSelection | null>(null);
  // 2. Abnahme, Befund 3: Der Aufklapp-Zustand des Baums ist EIGENER Zustand und wird NICHT mehr
  // aus der Auswahl abgeleitet.
  //
  // Vorher rendete die Prozessliste unter der Bedingung „links ist genau dieser Knoten
  // ausgewählt". Ein Klick auf einen Prozess setzt die Auswahl aber auf `{kind:'process'}` — und
  // damit klappte im selben Moment der Ast zu, aus dem der Nutzer gerade etwas gewählt hatte.
  // Der Baum sprang bei jeder Auswahl zurück in den Ausgangszustand.
  //
  // Zwei Zustände, zwei Variablen: `left` sagt, WAS rechts steht; `expandedNodes` sagt, was links
  // aufgeklappt ist. Eine Auswahl rechts fasst den Baum nicht mehr an.
  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(() => new Set());
  const [target, setTarget] = useState<string>('');
  // Die Vorschau merkt sich die Ids, für die sie berechnet wurde: Ausführen schickt GENAU diese. Aus
  // dem laufenden Zustand neu berechnet, könnte ein Hintergrund-Refetch bei offenem Dialog die
  // Menge ändern — dann würde etwas anderes umgehängt, als die Vorschau gezeigt hat.
  // Sie hält den GANZEN Rumpf, nicht nur die Ids: auch Name und Teilprojekt eines neuen Prozesses
  // müssen beim Ausführen dieselben sein wie in der Vorschau.
  const [preview, setPreview] = useState<{
    data: AssignmentPreview;
    body: AssignmentMoveBody;
  } | null>(null);
  // „Neuer Prozess …" (#153): Name und Teilprojekt gehören zur Zielauswahl und fallen mit ihr weg.
  // Das Teilprojekt wird aus der Auswahl vorbelegt, solange der Nutzer es nicht selbst gesetzt hat
  // (`newNodeTouched`) — danach gewinnt seine Wahl gegen jede spätere Vorbelegung.
  const [newName, setNewName] = useState('');
  const [newNodeId, setNewNodeId] = useState('');
  const [newNodeTouched, setNewNodeTouched] = useState(false);
  // Suche und Seitenleiste gehören zur aktuellen linken Auswahl und fallen bei jedem Wechsel weg
  // (`resetRight`) — ein Suchtext aus dem alten Prozess würde im neuen sonst still Zeilen verbergen.
  const [search, setSearch] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const showSourceRef = useShowSourceRef(projectId);
  // `createdName`: Name des Prozesses, den „Neuer Prozess …" gerade angelegt hat (Final-Review #153)
  // — die Ergebniszeile nennt ihn, damit der Nutzer weiß, wo seine Testfälle jetzt stehen.
  const [result, setResult] = useState<{
    ok: number;
    failedCount: number;
    revoked: number;
    createdName?: string;
  } | null>(null);

  const tree = useQuery({
    queryKey: ['catalog-tree', projectId],
    queryFn: () => catalogApi.tree(projectId),
    enabled: Boolean(projectId),
  });
  const nodes = useQuery({
    queryKey: ['structure-nodes', projectId],
    queryFn: () => structureApi.list(projectId),
    enabled: Boolean(projectId),
  });
  const e2e = useQuery({
    queryKey: ['catalog-e2e', projectId],
    queryFn: () => catalogApi.listE2e(projectId),
    enabled: Boolean(projectId) && view === 'stream',
  });
  // Testfälle werden NUR geladen, wenn links wirklich ein Prozess steht — die rechte Spalte zeigt
  // sonst Prozesse bzw. Ketten, und ein Vorratsladen aller 546 Testfälle wäre Aufwand ohne Anzeige.
  const testCases = useQuery({
    queryKey: ['catalog-testcases', projectId],
    queryFn: () => catalogApi.listTestCases(projectId),
    enabled: Boolean(projectId) && left?.kind === 'process',
  });

  // Die Kante ergibt sich aus der linken Auswahl — nicht aus einem eigenen Umschalter. Ein
  // separater „Was möchten Sie umhängen?"-Wähler wäre ein zweites Bedienelement für eine Frage,
  // die die Auswahl schon beantwortet hat.
  const kind: AssignmentKind =
    left?.kind === 'process' ? 'testcases' : view === 'stream' ? 'e2e' : 'process';

  // Auswahl leert sich, sobald sich Sicht oder linke Auswahl ändert — eine Sammelaktion darf nie
  // unsichtbar markierte Zeilen treffen (#91, useRowSelection).
  const selectionKey = `${view}|${left?.kind ?? ''}|${
    left?.kind === 'process' ? (left.processId ?? 'none') : (left?.nodeId ?? 'none')
  }`;
  const selection = useRowSelection(selectionKey);

  // Der Rumpf kommt fertig vom Aufrufer (`buildBody`: Ids = ausgewählt ∩ sichtbar, nicht aus
  // `selection.selected` — die Auswahl kennt auch ausgeblendete Zeilen).
  //
  // Fehler zeigt die Seite selbst statt des globalen Toasts (`errorToast: false`): die Zielfehler
  // des neuen Prozesses (Namensdublette, Knoten kein Teilprojekt) gehören neben die Felder, die der
  // Nutzer korrigieren muss; ein Toast verschwindet, bevor er sie gelesen hat.
  const previewMutation = useMutation({
    mutationFn: async (body: AssignmentMoveBody) => ({
      data: await assignmentApi.preview(projectId, body),
      body,
    }),
    meta: { errorToast: false },
    onSuccess: (p) => setPreview(p),
  });

  const applyMutation = useMutation({
    mutationFn: (body: AssignmentMoveBody) => assignmentApi.apply(projectId, body),
    // Der Dialog ist offen und überdeckt die Zeile — ein Fehler beim Ausführen (z. B. 409, weil
    // zwischen Vorschau und Klick jemand denselben Namen vergeben hat) steht deshalb im Dialog.
    meta: { errorToast: false },
    onSuccess: (data, body) => {
      setPreview(null);
      setNewName('');
      setNewNodeId('');
      setNewNodeTouched(false);
      // Der Name kommt aus dem gesendeten Body (die Felder sind gleich danach geleert), aber nur,
      // wenn der Server wirklich einen Prozess angelegt hat — bewegt sich nichts, entsteht keiner.
      // Die linke Auswahl springt bewusst NICHT in den neuen Prozess: der Nutzer arbeitet in der
      // Regel die Restliste („Nicht zugeordnet") weiter ab.
      setResult({
        ok: data.ok,
        failedCount: data.failed.length,
        revoked: data.revoked_count,
        ...(data.created_process_id && body.new_process
          ? { createdName: body.new_process.name_de }
          : {}),
      });
      selection.clear();
      void queryClient.invalidateQueries({ queryKey: ['catalog-tree', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-e2e', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['catalog-testcases', projectId] });
      // #153: ein neuer Prozess erscheint auch in der Prozessliste (Prozessauswahl im Katalog und
      // im Testfall-Detail), nicht nur im Baum.
      void queryClient.invalidateQueries({ queryKey: ['catalog-processes', projectId] });
      // #143: Prozess-/Teilprojekt-Zuordnung steckt in der Volliste des gefilterten Katalogbaums.
      void queryClient.invalidateQueries({ queryKey: ['tcs-search', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['structure-nodes', projectId] });
      void queryClient.invalidateQueries({ queryKey: ['signoffs', projectId] });
    },
  });

  if (projectLoading) return <div className="p-6 text-text-muted">{t('common.loading')}</div>;
  if (!project) return <div className="p-6 text-text-muted">{t('catalog.noProject')}</div>;

  const teilprojekte = (nodes.data ?? []).filter((n) => n.kind === 'teilprojekt');
  const streams = (nodes.data ?? []).filter((n) => n.kind === 'stream');
  const treeNodes = tree.data?.nodes ?? [];
  const unlabeledProcesses = tree.data?.unlabeled ?? [];

  // --- linke Spalte -----------------------------------------------------------------------------
  const leftRows: { key: string; label: string; count: number; sel: LeftSelection }[] =
    view === 'teilprojekt'
      ? [
          ...teilprojekte.map((n) => ({
            key: n.node_id,
            label: n.name,
            count: treeNodes.find((x) => x.node_id === n.node_id)?.processes.length ?? 0,
            sel: { kind: 'node', nodeId: n.node_id } as LeftSelection,
          })),
          // A2: Restmengen sind feste Abschnitte mit Stückzahl, nicht wegklickbar — und sie tragen
          // ZWEI verschiedene Namen, damit „Prozess ohne Teilprojekt" und „Testfall ohne Prozess"
          // nicht wieder unter einem Wort verschwimmen.
          {
            key: 'unlabeled',
            label: t('assignment.withoutTeilprojekt'),
            count: unlabeledProcesses.length,
            sel: { kind: 'node', nodeId: null } as LeftSelection,
          },
          {
            key: 'without-process',
            label: t('assignment.unassignedTestCases'),
            count: tree.data?.without_process ?? 0,
            sel: { kind: 'process', processId: null } as LeftSelection,
          },
        ]
      : [
          ...streams.map((n) => ({
            key: n.node_id,
            label: n.name,
            count: (e2e.data ?? []).filter((c) => c.node_id === n.node_id).length,
            sel: { kind: 'node', nodeId: n.node_id } as LeftSelection,
          })),
          {
            key: 'no-stream',
            label: t('assignment.withoutStream'),
            count: (e2e.data ?? []).filter((c) => c.node_id === null).length,
            sel: { kind: 'node', nodeId: null } as LeftSelection,
          },
        ];

  // Prozesse eines Teilprojekts sind zugleich linke Unterzeilen (Einstieg in Kante A) und rechte
  // Zeilen (Objekte der Kante B). Dieselbe Liste, ein Datenpfad.
  const processesOfSelectedNode = (nodeId: string | null) =>
    nodeId === null
      ? unlabeledProcesses
      : (treeNodes.find((n) => n.node_id === nodeId)?.processes ?? []);

  // --- rechte Spalte ----------------------------------------------------------------------------
  let allRows: RightRow[] = [];
  if (left?.kind === 'node' && view === 'teilprojekt') {
    allRows = processesOfSelectedNode(left.nodeId).map((p) => ({
      id: p.process_id,
      label: pickLang(lang, p.name_de, p.name_en),
      hint: p.source_ref ?? '',
      count: p.test_case_count,
      terms: [p.name_de, p.name_en, p.source_ref],
    }));
  } else if (left?.kind === 'node' && view === 'stream') {
    allRows = (e2e.data ?? [])
      .filter((c) => c.node_id === left.nodeId)
      .map((c) => ({
        id: c.e2e_id,
        label: pickLang(lang, c.name_de, c.name_en),
        hint: c.human_id,
        count: c.members.length,
        terms: [c.name_de, c.name_en, c.human_id, c.source_ref],
      }));
  } else if (left?.kind === 'process') {
    allRows = (testCases.data ?? [])
      .filter((tc) => tc.process_id === left.processId)
      .map((tc) => ({
        id: tc.test_case_id,
        label: pickLang(lang, tc.title_de, tc.title_en),
        hint: tc.human_id,
        count: null,
        terms: [
          tc.title_de,
          tc.title_en,
          tc.human_id,
          ...sourceSearchTerms(tc.source_ref, tc.chain_refs),
          // Final-Review #153: bei ausgeschalteter Quell-ID steht in der Ketten-Spalte die
          // TestFlow-ID der Kette — was in der Zeile steht, muss auch zu finden sein.
          ...tc.chain_refs.map((c) => c.human_id),
          tc.source_filename,
          // Das Import-Teilprojekt (Quelle-Spalte) ist das Merkmal, nach dem man unzugeordnete
          // Testfälle sortiert („alles aus Finanzen").
          tc.source_teilprojekt,
        ],
        tc,
      }));
  }
  const isTestCaseMode = left?.kind === 'process';
  // Suchfelder je Modus (`terms` oben): Quell-ID zählt auch bei ausgeschaltetem show_source_ref —
  // die Suche ist kein Anzeigeort. Der Platzhalter nennt nur, was im jeweiligen Modus wirklich
  // durchsucht wird.
  const searchPlaceholder = isTestCaseMode
    ? t('assignment.searchTestCases')
    : view === 'stream'
      ? t('assignment.searchChains')
      : t('assignment.searchProcesses');
  const rightRows = allRows.filter((r) => matchesQuery(search, ...r.terms));

  // Zielauswahl: dieselbe Liste, aus der links gewählt wird — nur ohne das aktuell offene Element.
  const targetOptions =
    kind === 'testcases'
      ? treeNodes
          .flatMap((n) => n.processes)
          .concat(unlabeledProcesses)
          .map((p) => ({ id: p.process_id, label: pickLang(lang, p.name_de, p.name_en) }))
      : kind === 'process'
        ? teilprojekte.map((n) => ({ id: n.node_id, label: n.name }))
        : streams.map((n) => ({ id: n.node_id, label: n.name }));

  const toggleExpanded = (key: string) =>
    setExpandedNodes((prev) => {
      const next = new Set(prev);
      if (!next.delete(key)) next.add(key);
      return next;
    });

  // Jeder Wechsel der linken Auswahl/Sicht verwirft Suche, Seitenleiste und die Felder des neuen
  // Prozesses mit (siehe oben) — sie gehören zur Zielauswahl, und die wird bei jedem Wechsel
  // ebenfalls zurückgesetzt. Ein stehengebliebener Fehler klebte sonst an einem Ziel, das es nicht
  // mehr gibt.
  const resetRight = () => {
    setSearch('');
    setDetailId(null);
    setNewName('');
    setNewNodeId('');
    setNewNodeTouched(false);
    previewMutation.reset();
  };

  const visibleIds = rightRows.map((r) => r.id);
  // Nur Sichtbares zählt (#91): die Auswahl behält ausgeblendete Zeilen, handeln darf sie nicht.
  const selectedIds = visibleIds.filter((id) => selection.isSelected(id));
  const allVisibleSelected = visibleIds.length > 0 && selectedIds.length === visibleIds.length;

  // Neuer Prozess (#153): nur für Testfälle möglich (Kante A). Die Vorbelegung wird bei jedem Render
  // aus der Auswahl abgeleitet statt in einen Effekt gespiegelt — so gibt es keinen Zustand, der der
  // Auswahl hinterherläuft.
  const isNewProcess = kind === 'testcases' && target === NEW_PROCESS_TARGET;
  const selectedTestCases = rightRows
    .filter((r) => selection.isSelected(r.id))
    .flatMap((r) => (r.tc ? [r.tc] : []));
  const effectiveNodeId = newNodeTouched
    ? newNodeId
    : (prefillTeilprojekt(selectedTestCases, teilprojekte) ?? '');
  const newProcessReady = newName.trim() !== '' && effectiveNodeId !== '';

  // EIN Rumpf für Vorschau und Ausführen. Beim neuen Prozess ist das Ziel `null` + `new_process`
  // (der Server lehnt beides zugleich ab).
  const buildBody = (ids: string[]): AssignmentMoveBody =>
    isNewProcess
      ? {
          kind,
          ids,
          target_id: null,
          new_process: { name_de: newName.trim(), node_id: effectiveNodeId },
        }
      : { kind, ids, target_id: target === '' ? null : target };
  // Ändert der Nutzer Ziel oder Felder, gilt ein früherer Fehler nicht mehr.
  const clearPreviewError = () => previewMutation.reset();

  return (
    // Feste Höhe wie im Katalog (catalog-page.tsx): `h-full` griff ins Leere, weil <main> keine
    // Höhe hat — die Seite wuchs mit der Liste, und die Bewegungsleiste stand unter 64 Zeilen.
    <div className="flex h-[calc(100vh-58px)] flex-col">
      <div className="border-b border-[var(--border)] px-6 py-4">
        <h1 className="text-lg font-bold text-text-primary">{t('assignment.title')}</h1>
        <p className="mt-1 text-xs text-text-muted">{t('assignment.hint')}</p>
        <div className="mt-3 flex gap-2 text-sm">
          {(['teilprojekt', 'stream'] as View[]).map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => {
                setViewParam(v);
                setLeft(null);
                setTarget('');
                resetRight();
              }}
              aria-pressed={view === v}
              className={`rounded-chip border px-3 py-1 ${
                view === v
                  ? 'border-brand-accent text-brand-accent'
                  : 'border-[var(--border)] text-text-muted'
              }`}
            >
              {t(`assignment.view.${v}`)}
            </button>
          ))}
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden">
        {/* Struktur links */}
        <nav
          aria-label={t('assignment.structure')}
          className="w-72 shrink-0 overflow-y-auto border-r border-[var(--border)] p-3"
        >
          <ul className="space-y-1">
            {leftRows.map((row) => {
              const active =
                left !== null &&
                ((row.sel.kind === 'node' &&
                  left.kind === 'node' &&
                  left.nodeId === row.sel.nodeId) ||
                  (row.sel.kind === 'process' &&
                    left.kind === 'process' &&
                    left.processId === row.sel.processId));
              // Nur Knotenzeilen der Prozess-Sicht tragen eine zweite Ebene (Prozesse). Die
              // Restmenge „Nicht zugeordnet" ist selbst schon eine Prozess-Auswahl, Streams haben
              // in dieser Sicht keine Unterebene.
              const expandable = row.sel.kind === 'node' && view === 'teilprojekt';
              const isExpanded = expandable && expandedNodes.has(row.key);
              return (
                <li key={row.key}>
                  {/* Ein <button> darf keinen <button> enthalten — deshalb ist die Zeile ein
                      <div> mit zwei Schaltflächen darin (Muster aus process-tree.tsx). Aufklappen
                      und Auswählen sind seit Befund 3 getrennte Handlungen: der Pfeil klappt auf
                      und zu, der Name wählt aus. */}
                  <div
                    className={`flex w-full items-center gap-1 rounded-btn px-1 ${
                      active ? 'bg-brand-accent/10 ring-1 ring-inset ring-brand-accent' : ''
                    }`}
                  >
                    {expandable ? (
                      <button
                        type="button"
                        onClick={() => toggleExpanded(row.key)}
                        aria-expanded={isExpanded}
                        aria-label={t('assignment.toggleNode', { name: row.label })}
                        className="shrink-0 px-1 text-xs text-text-muted hover:text-text-body"
                      >
                        {isExpanded ? '▾' : '▸'}
                      </button>
                    ) : (
                      <span className="w-[1.375rem] shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={() => {
                        setLeft(row.sel);
                        setTarget('');
                        setResult(null);
                        resetRight();
                        // Auswählen klappt auf, aber nie zu: „zeig mir, was drinsteckt" ist die
                        // Absicht hinter dem Klick. Zuklappen bleibt dem Pfeil vorbehalten, damit
                        // ein zweiter Klick auf denselben Knoten den Ast nicht wegnimmt.
                        if (expandable) {
                          setExpandedNodes((prev) => new Set(prev).add(row.key));
                        }
                      }}
                      aria-pressed={active}
                      className={`flex min-w-0 flex-1 items-center justify-between py-1.5 text-left text-sm ${
                        active ? 'text-brand-accent' : 'text-text-body'
                      }`}
                    >
                      <span className="truncate">{row.label}</span>
                      <span className="ml-2 shrink-0 text-xs text-text-muted">{row.count}</span>
                    </button>
                  </div>
                  {/* Prozesse als zweite Ebene: nur so kommt man an Kante A (Testfälle) heran.
                      Sichtbarkeit hängt jetzt an `expandedNodes`, NICHT mehr an `left` — sonst
                      klappt der Ast beim Klick auf einen seiner Prozesse zu (Befund 3). */}
                  {expandable && isExpanded && row.sel.kind === 'node' && (
                    <ul className="ml-3 mt-1 space-y-1 border-l border-[var(--border)] pl-2">
                      {processesOfSelectedNode(row.sel.nodeId).map((p) => {
                        const processActive =
                          left?.kind === 'process' && left.processId === p.process_id;
                        return (
                          <li key={p.process_id}>
                            <button
                              type="button"
                              onClick={() => {
                                setLeft({ kind: 'process', processId: p.process_id });
                                setTarget('');
                                setResult(null);
                                resetRight();
                              }}
                              aria-pressed={processActive}
                              // Dieselbe Hervorhebung wie im Katalog-Prozessbaum
                              // (process-tree.tsx): der gewählte Prozess muss links sichtbar
                              // bleiben, während rechts seine Testfälle stehen (Befund 3).
                              className={`flex w-full items-center justify-between rounded-btn px-2 py-1 text-left text-xs ${
                                processActive
                                  ? 'bg-brand-accent/10 text-brand-accent ring-1 ring-inset ring-brand-accent'
                                  : 'text-text-muted hover:text-text-body'
                              }`}
                            >
                              <span className="truncate">
                                {pickLang(lang, p.name_de, p.name_en)}
                              </span>
                              <span className="ml-2 shrink-0">{p.test_case_count}</span>
                            </button>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              );
            })}
          </ul>
        </nav>

        {/* Inhalte rechts */}
        <section className="flex flex-1 overflow-hidden">
          {left === null ? (
            <div className="p-6 text-sm text-text-muted">{t('assignment.selectHint')}</div>
          ) : (
            <div className="flex min-w-0 flex-1 flex-col overflow-hidden">
              <div className="border-b border-[var(--border)] px-3 py-2">
                <input
                  type="search"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  aria-label={t('assignment.search')}
                  placeholder={searchPlaceholder}
                  className="w-full max-w-sm rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
                />
              </div>
              <div className="flex-1 overflow-y-auto p-3">
                {rightRows.length === 0 ? (
                  <div className="text-sm text-text-muted">
                    {allRows.length === 0 ? t('assignment.empty') : t('assignment.noMatches')}
                  </div>
                ) : (
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b border-[var(--border)] text-left text-xs text-text-muted">
                        <th className="w-8 p-2">
                          <input
                            type="checkbox"
                            aria-label={t('assignment.selectAll')}
                            checked={allVisibleSelected}
                            onChange={(e) => selection.setMany(visibleIds, e.target.checked)}
                          />
                        </th>
                        {isTestCaseMode ? (
                          <TestCaseHeadCells compact={detailId !== null} />
                        ) : (
                          <>
                            <th className="p-2">{t('assignment.name')}</th>
                            <th className="w-32 p-2 text-right">{t('assignment.count')}</th>
                          </>
                        )}
                      </tr>
                    </thead>
                    <tbody>
                      {rightRows.map((row) => (
                        <tr
                          key={row.id}
                          className={`border-b border-[var(--border)] ${
                            detailId === row.id ? 'bg-brand-accent/10' : ''
                          }`}
                        >
                          <td className="p-2 align-top">
                            <input
                              type="checkbox"
                              aria-label={row.label}
                              checked={selection.isSelected(row.id)}
                              onChange={() => selection.toggle(row.id)}
                            />
                          </td>
                          {row.tc ? (
                            <TestCaseCells
                              tc={row.tc}
                              active={detailId === row.id}
                              showSourceRef={showSourceRef}
                              compact={detailId !== null}
                              onOpen={() => setDetailId(row.id)}
                            />
                          ) : (
                            <>
                              <td className="p-2 text-text-body">
                                <span>{row.label}</span>
                                {row.hint && (
                                  <span className="ml-2 text-xs text-text-muted">{row.hint}</span>
                                )}
                              </td>
                              <td className="p-2 text-right text-xs text-text-muted">
                                {row.count === null
                                  ? ''
                                  : t('assignment.testCaseCount', { count: row.count })}
                              </td>
                            </>
                          )}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              {/* Ein Bedienmuster für alle drei Kanten: Ziel wählen → Vorschau → ausführen. */}
              <div className="flex flex-wrap items-center gap-2 border-t border-[var(--border)] p-3">
                <span className="text-xs text-text-muted">
                  {t('assignment.selected', { count: selectedIds.length })}
                </span>
                <select
                  aria-label={t('assignment.target')}
                  value={target}
                  onChange={(e) => {
                    setTarget(e.target.value);
                    clearPreviewError();
                  }}
                  // Ohne Obergrenze richtet sich ein natives <select> nach dem längsten Prozessnamen
                  // und ragte bei 1280 px über den Rand hinaus.
                  className="min-w-0 max-w-xs rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
                >
                  {/* Leerer Wert = Zuordnung lösen. A2 lässt Unzugeordnetes ausdrücklich zu, also
                      ist das ein reguläres Ziel und kein Sonderfall im Kleingedruckten. */}
                  <option value="">{t('assignment.targetNone')}</option>
                  {/* Nur bei Testfällen (#153): „Neuer Prozess …" statt erst im Admin-Panel einen
                      anzulegen — dort bliebe bei Abbruch ein leerer Prozess zurück. */}
                  {kind === 'testcases' && (
                    <option value={NEW_PROCESS_TARGET}>{t('assignment.targetNew')}</option>
                  )}
                  {targetOptions.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.label}
                    </option>
                  ))}
                </select>
                {isNewProcess && (
                  <>
                    <input
                      type="text"
                      value={newName}
                      maxLength={200}
                      onChange={(e) => {
                        setNewName(e.target.value);
                        clearPreviewError();
                      }}
                      aria-label={t('assignment.newProcess.name')}
                      placeholder={t('assignment.newProcess.name')}
                      className="w-56 rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
                    />
                    <select
                      value={effectiveNodeId}
                      onChange={(e) => {
                        setNewNodeId(e.target.value);
                        setNewNodeTouched(true);
                        clearPreviewError();
                      }}
                      aria-label={t('assignment.newProcess.teilprojekt')}
                      className="rounded-btn border border-[var(--border)] bg-bg-surface px-2 py-1 text-sm text-text-primary"
                    >
                      <option value="">{t('assignment.newProcess.teilprojektPlaceholder')}</option>
                      {teilprojekte.map((n) => (
                        <option key={n.node_id} value={n.node_id}>
                          {n.name}
                        </option>
                      ))}
                    </select>
                  </>
                )}
                <button
                  type="button"
                  disabled={
                    selectedIds.length === 0 ||
                    previewMutation.isPending ||
                    (isNewProcess && !newProcessReady)
                  }
                  onClick={() => {
                    applyMutation.reset();
                    previewMutation.mutate(buildBody(selectedIds));
                  }}
                  className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
                >
                  {t('assignment.preview')}
                </button>
                {result && (
                  <span aria-live="polite" className="text-xs text-text-body">
                    {/* Beide Sätze teilen den Nachsatz (übersprungen/widerrufen) über
                        $t(assignment.resultRest) — eine Formulierung, zwei Einstiege. */}
                    {result.createdName !== undefined
                      ? t('assignment.resultNewProcess', {
                          count: result.ok,
                          ok: result.ok,
                          name: result.createdName,
                          failed: result.failedCount,
                          revoked: result.revoked,
                        })
                      : t('assignment.result', {
                          ok: result.ok,
                          failed: result.failedCount,
                          revoked: result.revoked,
                        })}
                  </span>
                )}
                {previewMutation.error && (
                  <p role="alert" className="basis-full text-xs text-status-fail">
                    {mapApiError(t, previewMutation.error)}
                  </p>
                )}
              </div>
            </div>
          )}

          {left !== null && detailId !== null && (
            <TestCaseSidePanel
              projectId={projectId}
              testCaseId={detailId}
              onClose={() => setDetailId(null)}
            />
          )}
        </section>
      </div>

      {preview && (
        <PreviewDialog
          preview={preview.data}
          newProcessTeilprojekt={
            preview.body.new_process
              ? (teilprojekte.find((n) => n.node_id === preview.body.new_process?.node_id)?.name ??
                '')
              : null
          }
          pending={applyMutation.isPending}
          error={applyMutation.error ? mapApiError(t, applyMutation.error) : null}
          onCancel={() => {
            setPreview(null);
            applyMutation.reset();
          }}
          onConfirm={() => applyMutation.mutate(preview.body)}
        />
      )}
    </div>
  );
}

/**
 * Die Vorschau nennt drei Dinge, bevor irgendetwas passiert: wie viele Testfälle betroffen sind,
 * von wo nach wo es geht, und welche Bestätigungen dabei fallen (A7/A8). Die Zahlen kommen aus
 * derselben Serverrechnung, die `apply` gleich ausführt — die Vorschau kann also nicht etwas
 * anderes zeigen als das, was passiert.
 */
function PreviewDialog({
  preview,
  newProcessTeilprojekt,
  pending,
  error,
  onCancel,
  onConfirm,
}: {
  preview: AssignmentPreview;
  /** Teilprojekt des neu anzulegenden Prozesses; `null` = das Ziel ist ein vorhandenes. */
  newProcessTeilprojekt: string | null;
  pending: boolean;
  /** Fehler des Ausführens (übersetzt) — der Dialog bleibt dafür offen. */
  error: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const { t } = useTranslation();
  const name = (n: string | null) => n ?? t('assignment.none');
  // Der neue Prozess hat noch keine Id, aber der Nutzer muss lesen können, WAS entsteht und WO:
  // Name und Teilprojekt stehen im Ziel selbst, nicht im Kleingedruckten.
  const target =
    newProcessTeilprojekt !== null
      ? t('assignment.previewTargetNew', {
          name: preview.to.name ?? '',
          teilprojekt: newProcessTeilprojekt,
        })
      : name(preview.to.name);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
      <button
        type="button"
        aria-label={t('common.close')}
        onClick={onCancel}
        className="absolute inset-0"
      />
      <dialog
        open
        aria-modal="true"
        aria-label={t('assignment.previewTitle')}
        className="relative z-10 m-0 w-full max-w-lg space-y-3 rounded-card border border-[var(--border)] bg-bg-surface p-4 text-text-body shadow-xl"
      >
        <h2 className="text-sm font-semibold text-text-primary">{t('assignment.previewTitle')}</h2>
        <p className="text-sm">{t('assignment.previewCount', { count: preview.moved_count })}</p>
        <p className="text-sm text-text-muted">
          {t('assignment.previewRoute', {
            from: preview.from.map((f) => name(f.name)).join(', ') || name(null),
            to: target,
          })}
        </p>
        {preview.signoffs_revoked.length > 0 && (
          <div className="rounded-card border border-status-fail/40 p-2 text-sm text-status-fail">
            <p>{t('assignment.previewRevoke', { count: preview.signoffs_revoked.length })}</p>
            <ul className="mt-1 list-inside list-disc text-xs">
              {preview.signoffs_revoked.map((s) => (
                <li key={s.signoff_id}>
                  {s.node_name} · {s.plan_name}
                </li>
              ))}
            </ul>
          </div>
        )}
        {preview.failed.length > 0 && (
          <p className="text-xs text-text-muted">
            {t('assignment.previewSkipped', { count: preview.failed.length })}
          </p>
        )}
        {/* Kein Undo — deshalb steht der Hinweis HIER, vor dem Klick, und nicht danach. */}
        <p className="text-xs text-text-muted">{t('assignment.noUndo')}</p>
        {error && (
          <p role="alert" className="text-xs text-status-fail">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-btn px-3 py-1.5 text-sm text-text-muted hover:text-text-body"
          >
            {t('common.cancel')}
          </button>
          {/* Gesperrt wird an `moved_ids`, NICHT an `moved_count` (Gesamtreview-Befund, TF-006):
              `moved_count` zählt bei den Kanten B und C die betroffenen TESTFÄLLE, nicht die
              bewegten Objekte. Ein frisch angelegter Prozess ohne Testfälle (oder eine Kette ohne
              Mitglieder) steht in `moved_ids`, bringt aber `moved_count: 0` mit — der Knopf blieb
              grau, ohne dass irgendwo stand warum. `moved_ids` ist die Menge, die `apply` bewegt;
              genau sie entscheidet, ob es etwas auszuführen gibt. */}
          <button
            type="button"
            disabled={pending || preview.moved_ids.length === 0}
            onClick={onConfirm}
            className="rounded-btn bg-brand-accent px-3 py-1.5 text-sm text-white disabled:opacity-60"
          >
            {t('assignment.apply')}
          </button>
        </div>
      </dialog>
    </div>
  );
}
